import IWithSiteLink from '@/data/dtos/IWithSiteLink';
import DateUtils from '@/utils/DateUtils';
import IWithLocale from '@/data/dtos/IWithLocale';
import { EventCategoryDTO } from '@/data/dtos/Events/EventCategoryDTO';
import { EventInstructorDTO } from '@/data/dtos/Events/EventInstructorDTO';
import { EventAgendaDayDTO, EventAgendaDTO } from '@/data/dtos/Events/EventAgendaDTO';
import { EventLocationDTO } from '@/data/dtos/Events/EventLocationDTO';
import { EventOrganizerDTO } from '@/data/dtos/Events/EventOrganizerDTO';
import { EventTicketDTO } from '@/data/dtos/Events/EventTicketDTO';
import { EventFAQType } from '@/data/dtos/Events/EventFAQType';
import { EventExpertLevelDTO } from '@/data/dtos/Events/EventExpertLevelDTO';
import { EventSessionDTO } from '@/data/dtos/Events/EventSessionDTO';
import { EventTagDTO } from '@/data/dtos/Events/EventTagDTO';
import { EventSwimlineDTO } from '@/data/dtos/Events/EventSwimlineDTO';
import StringUtils from '@/utils/StringUtils';
import {EventBookType} from "@/data/dtos/Events/EventBookType";
import Serializable from '@/data/dtos/Serializable';
import {EventFormatType} from "@/data/dtos/Events/EventFormatType";
import { EventGalleryDTO } from '@/data/dtos/Events/EventGalleryDTO';
import { EventFullLanguageDTO } from '@/data/dtos/Events/EventFullLanguageDTO';

export class EventDTO extends Serializable<EventTicketDTO> implements IWithSiteLink, IWithLocale {

	id: string;
	status: 'completed' | 'upcoming' | 'canceled';
	visibility: number;
	verified: boolean;
	featured: boolean;
	slug: string;
	title: string;
	description: string;
	format: EventFormatType;
	dateStart: string;
	dateEnd: string;
	languages: EventFullLanguageDTO[];
	book: EventBookType;
	bookRedirectLink: string | null;
	spotsMax: number | null;
	spotsCurrent: number | null;
	spotsLeft: number | null;
	priceCurrency: string | null;
	categories: EventCategoryDTO[];
	organizers: EventOrganizerDTO[];
	instructors: EventInstructorDTO[];
	brands: EventOrganizerDTO[] | null;
	locations: EventLocationDTO[];
	tickets: EventTicketDTO[] | null;
	faq: EventFAQType[] | null;
	agenda: EventAgendaDTO | null;

	reviews: EventGalleryDTO | null;
	gallery: EventGalleryDTO | null;
	whyAttend: EventGalleryDTO | null;

	expertiseLevels: EventExpertLevelDTO[];

	siteLink: string;
	locale: string;

	related: EventDTO[]|null;
	bundleOffer: null;

	calendars: {
		google: string;
		outlook: string;
		apple: string;
	};

	constructor(data: Partial<EventDTO>) {
		super();
		
		this.id = data.id!;

		this.status = data.status!;
		this.visibility = data.visibility!;
		this.verified = data.verified!;
		this.featured = data.featured!;
		this.slug = data.slug!;
		this.title = data.title!;
		this.description = data.description!;
		this.format = data.format!;
		this.dateStart = data.dateStart!;
		this.dateEnd = data.dateEnd!;
		this.languages = data.languages!;
		this.book = data.book!;
		this.bookRedirectLink = data.bookRedirectLink || null;
		this.spotsMax = data.spotsMax !== undefined ? data.spotsMax : null;
		this.spotsCurrent = data.spotsCurrent !== undefined ? data.spotsCurrent : null;
		this.spotsLeft = data.spotsLeft !== undefined ? data.spotsLeft : null;
		
		this.priceCurrency = data.priceCurrency || null;
		this.categories = data.categories!;
		this.organizers = data.organizers!;
		this.instructors = data.instructors!;
		
		this.brands = data.brands || null;
		this.locations = data.locations!;
		this.tickets = data.tickets || null;
		this.faq = data.faq || null;
		this.agenda = data.agenda || null;
		this.expertiseLevels = data.expertiseLevels!;

		this.reviews = data.reviews || null;
		this.gallery = data.gallery || null;
		this.whyAttend = data.whyAttend || null;

		this.siteLink = data.siteLink!;
		this.locale = data.locale!;

		this.related = null;
		this.bundleOffer = null;

		this.calendars = data.calendars || {
			google: '',
			outlook: '',
			apple: '',
		};
	}

	public isVisible(): boolean {
		return this.visibility == 1;
	}
	public isUnlisted(): boolean {
		return this.visibility == 10;
	}

	public getInstructorById(instructorId: string): EventInstructorDTO | null {
		for (const instuctorDTO of this.instructors) {
			if (instuctorDTO.slug == instructorId) {
				return instuctorDTO;
			}
		}
		return null;
	}

	private _priceMin: number | null = null;

	public getPriceMin(): number {
		if (this._priceMin === null) {
			// If tickets are null or undefined, set priceMin to 0 (free)
			if (!this.tickets || this.tickets.length === 0) {
				this._priceMin = 0;
			} else {
				// Filter out expired tickets
				const validTickets = this.tickets.filter(ticket =>
					ticket.expiresAt == null || new Date(ticket.expiresAt) > new Date()
				);

				// If no valid tickets, set priceMin to 0 (free)
				if (validTickets.length === 0) {
					this._priceMin = 0;
				} else {
					// Find the minimum price among valid tickets
					const prices = validTickets.map(ticket => ticket.price);
					this._priceMin = Math.min(...prices);
				}
			}
		}

		return this._priceMin;
	}

	private _priceMax: number | null = null;

	public getPriceMax(): number {
		if (this._priceMax === null) {
			// If tickets are null or undefined, set priceMin to 0 (free)
			if (!this.tickets || this.tickets.length === 0) {
				this._priceMax = 0;
			} else {
				// Filter out expired tickets
				const validTickets = this.tickets.filter(ticket =>
					ticket.expiresAt == null || new Date(ticket.expiresAt) > new Date()
				);

				// If no valid tickets, set priceMin to 0 (free)
				if (validTickets.length === 0) {
					this._priceMax = 0;
				} else {
					// Find the minimum price among valid tickets
					const prices = validTickets.map(ticket => ticket.price);
					this._priceMax = Math.max(...prices);
				}
			}
		}
		return this._priceMax;
	}

	public getDates(): string {
		return DateUtils.formatRangeDate(this.dateStart, this.dateEnd, this.locale);
	}

	public isPassed(): boolean {
		const currentDate = new Date();
		const eventEndDate = new Date(this.dateEnd);
		// Check if the current date is greater than the end date
		return currentDate.getTime() > eventEndDate.getTime();
	}

	public isOngoing(): boolean {
		const currentDate = new Date();
		const eventStartDate = new Date(this.dateStart);
		const eventEndDate = new Date(this.dateEnd);
		// Check if the current date is between the start and end dates
		return currentDate.getTime() >= eventStartDate.getTime() && currentDate.getTime() <= eventEndDate.getTime();
	}

	public getOrganizer(): EventOrganizerDTO {
		for (const organizerDTO of this.organizers) {
			if (organizerDTO.verified) {
				return organizerDTO;
			}
		}
		return this.organizers[0];
	}

	public getLocation(): EventLocationDTO {
		return this.locations[0];
	}

	public showSpotsLeft():boolean {
		if (this.spotsLeft == 0 || this.spotsLeft == null || this.spotsMax == null) {
			return false;
		} 

		return (this.spotsLeft <= 5 || (this.spotsLeft / this.spotsMax) <= 0.2);
	}

	public getPriceLabel(freeLabel: string = 'Безплатно', startingAtLabel: string = 'Започва от'): string {
		const minPrice = this.getPriceMin();
		const maxPrice =this.getPriceMax();

		if (minPrice == 0 && maxPrice == 0) {
			return freeLabel;
		}

		let priceLabel = '';
		// Determine the price label based on the price range
		if (minPrice === maxPrice) {
			priceLabel = StringUtils.formatCurrency(minPrice, this.priceCurrency ?? '', this.locale);
		} else {
			priceLabel = `${startingAtLabel} ${StringUtils.formatCurrency(minPrice, this.priceCurrency ?? '', this.locale)}`;
		}

		return priceLabel;
	}

	public static fromArray(jsonEventData: any, locale: string): EventDTO {
		const instructors: EventInstructorDTO[] = jsonEventData.instructors.map((instructor: any) => EventInstructorDTO.fromArray(instructor, locale));

		// Map instructors for quick lookup by slug
		const instructorsMap = new Map(instructors.map((instructor: EventInstructorDTO) => [instructor.slug, instructor]));

		let agendaDTO: EventAgendaDTO | null = null;
		if (jsonEventData.agenda) {
			let loopAgendaDays = jsonEventData.agenda;
			if (!Array.isArray(loopAgendaDays)) {
				loopAgendaDays = loopAgendaDays.days;
			}

			const days: EventAgendaDayDTO[] = loopAgendaDays.map((daysData: any) => {
				const swimlines: EventSwimlineDTO[] = daysData.swimlines.map((swimlineData: any) => {
					const sessions: EventSessionDTO[] = swimlineData.sessions.map((sessionData: any) => {
						const tempInstructors: EventInstructorDTO[] = sessionData.instructorIds?.map((id: string) => instructorsMap.get(id)).filter((instructor: EventInstructorDTO | undefined): instructor is EventInstructorDTO => instructor !== undefined);

						const tags: EventTagDTO[] | null = sessionData.tags ? sessionData.tags.map((tag: any) => new EventTagDTO(tag)) : null;

						return new EventSessionDTO(sessionData.multiLine, sessionData.from, sessionData.to, sessionData.title, sessionData.description || null, tempInstructors || null, tags);
					});

					return new EventSwimlineDTO(swimlineData.title, swimlineData.description || null, sessions);
				});

				return new EventAgendaDayDTO(
					new Date(daysData.date), // Assuming date is in ISO format
					swimlines,
				);
			});

			agendaDTO = new EventAgendaDTO(days);
		}

		const tickets: EventTicketDTO[]|null = jsonEventData.tickets?.map((ticket: any) => {
			return EventTicketDTO.fromJSON(ticket);
		}) || null;

		const categories: EventCategoryDTO[]|null = jsonEventData.categories?.map((category: EventCategoryDTO) => ({
			...category,
			siteLink: `/events/?category=${category.slug}`,
		})) || [];

		return new EventDTO({
			...jsonEventData,
			siteLink: `/event/${jsonEventData.slug}`,
			tickets: tickets,
			categories: categories,
			organizers:
				jsonEventData.organizers?.map((organizer: EventOrganizerDTO) => ({
					...organizer,
					siteLink: `/events/${organizer.slug}`,
				})) || [],
			brands: jsonEventData.brands?.length
				? jsonEventData.brands.map((brand: EventOrganizerDTO) => ({
						...brand,
						siteLink: `/events/${brand.slug}`,
					}))
				: null,
			instructors:
				jsonEventData.instructors?.map((instructor: EventInstructorDTO) => ({
					...instructor,
					siteLink: `/expert/${instructor.slug}`,
				})) || [],
			locations: jsonEventData.locations.map((location: EventLocationDTO) => ({
				...location,
			})),
			agenda: agendaDTO,
			locale: locale,
			calendars: jsonEventData.calendars || null,
		});
	}
}
